/**
 * Configuration contextuelle de l'Assistant Abracada
 * Adapte automatiquement l'assistant selon la page active
 */

import { 
  CheckSquare, Mail, MessageSquare, Calendar, Video, FileText, 
  Lightbulb, FolderOpen, Users, Globe, ShoppingBag, Wrench,
  UserCheck, FileSignature, Receipt, Store, Package, Share2,
  UsersRound, Bot, Briefcase, Clock
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// MODULES PARTICULIER (Abracada)
// ═══════════════════════════════════════════════════════════════════════════

export const assistantContextConfig = {
  
  // ─────────────────────────────────────────────────────────────────────────
  // LISTE DES TÂCHES
  // ─────────────────────────────────────────────────────────────────────────
  tasks: {
    key: 'tasks',
    icon: CheckSquare,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider sur tes tâches",
    intro: "Tu es dans **Mon Espace > Liste des Tâches**. Je peux t'aider à créer, organiser, prioriser ou automatiser tes tâches.",
    placeholder: "Demande-moi d'organiser tes tâches…",
    quickActions: [
      { id: 'create_task', label: "Créer une tâche", icon: "plus" },
      { id: 'summarize_priorities', label: "Résumer mes priorités", icon: "zap" },
      { id: 'sort_urgent', label: "Classer par urgence", icon: "alert-triangle" },
      { id: 'late_tasks', label: "Voir les retards", icon: "clock" },
      { id: 'plan_day', label: "Planifier ma journée", icon: "calendar" }
    ],
    examples: [
      "Ajoute une tâche pour rappeler Pierre demain.",
      "Classe mes tâches par priorité.",
      "Qu'est-ce que je dois faire aujourd'hui en premier ?",
      "Crée une tâche à partir de ce message."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MES MESSAGES
  // ─────────────────────────────────────────────────────────────────────────
  messages: {
    key: 'messages',
    icon: MessageSquare,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes messages",
    intro: "Tu es dans **Mes Messages**. Je peux t'aider à lire, résumer, classer ou préparer des réponses.",
    placeholder: "Demande-moi de t'aider avec tes messages…",
    quickActions: [
      { id: 'summarize_messages', label: "Résumer mes messages", icon: "file-text" },
      { id: 'urgent_messages', label: "Messages urgents", icon: "alert-circle" },
      { id: 'prepare_reply', label: "Préparer une réponse", icon: "reply" },
      { id: 'message_to_task', label: "Créer une tâche", icon: "check-square" },
      { id: 'follow_up', label: "Relancer", icon: "send" }
    ],
    examples: [
      "Résume mes derniers messages.",
      "Quels messages demandent une réponse rapide ?",
      "Prépare une réponse polie à ce message.",
      "Transforme ce message en tâche."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MES EMAILS
  // ─────────────────────────────────────────────────────────────────────────
  emails: {
    key: 'emails',
    icon: Mail,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes emails",
    intro: "Tu es dans **Mes Emails**. Je peux t'aider à résumer, rédiger, classer ou répondre à tes emails.",
    placeholder: "Demande-moi de résumer ou répondre à un email…",
    quickActions: [
      { id: 'summarize_emails', label: "Résumer les importants", icon: "file-text" },
      { id: 'prepare_reply', label: "Préparer une réponse", icon: "reply" },
      { id: 'find_urgent', label: "Trouver les urgences", icon: "alert-circle" },
      { id: 'classify', label: "Classer les emails", icon: "folder" },
      { id: 'email_to_task', label: "Créer une tâche", icon: "check-square" }
    ],
    examples: [
      "Résume mes emails importants.",
      "Prépare une réponse professionnelle.",
      "Quels emails sont urgents ?",
      "Transforme cet email en tâche."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MON AGENDA
  // ─────────────────────────────────────────────────────────────────────────
  agenda: {
    key: 'agenda',
    icon: Calendar,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec ton agenda",
    intro: "Tu es dans **Mon Agenda**. Je peux t'aider à organiser ton planning, trouver un créneau ou préparer ta journée.",
    placeholder: "Demande-moi de gérer ton agenda…",
    quickActions: [
      { id: 'summarize_day', label: "Résumer ma journée", icon: "sun" },
      { id: 'find_slot', label: "Trouver un créneau", icon: "search" },
      { id: 'add_event', label: "Ajouter un RDV", icon: "plus" },
      { id: 'move_event', label: "Déplacer un événement", icon: "move" },
      { id: 'plan_week', label: "Préparer ma semaine", icon: "calendar" }
    ],
    examples: [
      "Ajoute un rendez-vous demain à 15h.",
      "Trouve-moi un créneau libre cette semaine.",
      "Résume ma journée.",
      "Prépare mon planning de demain."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // VISIOCONFÉRENCE
  // ─────────────────────────────────────────────────────────────────────────
  visio: {
    key: 'visio',
    icon: Video,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes réunions",
    intro: "Tu es dans **Visioconférence**. Je peux t'aider à préparer, lancer, résumer ou organiser tes réunions.",
    placeholder: "Demande-moi de préparer une visio…",
    quickActions: [
      { id: 'create_visio', label: "Créer une visio", icon: "video" },
      { id: 'prepare_agenda', label: "Préparer l'ordre du jour", icon: "list" },
      { id: 'summarize_meeting', label: "Résumer une réunion", icon: "file-text" },
      { id: 'send_summary', label: "Envoyer un compte rendu", icon: "send" },
      { id: 'schedule_meeting', label: "Planifier une réunion", icon: "calendar" }
    ],
    examples: [
      "Crée une visio avec mon équipe.",
      "Prépare un ordre du jour.",
      "Fais le résumé de cette réunion.",
      "Crée les tâches à partir du compte rendu."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MES NOTES
  // ─────────────────────────────────────────────────────────────────────────
  notes: {
    key: 'notes',
    icon: FileText,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes notes",
    intro: "Tu es dans **Mes Notes**. Je peux t'aider à structurer, résumer, reformuler ou transformer tes notes en actions.",
    placeholder: "Demande-moi de structurer tes notes…",
    quickActions: [
      { id: 'summarize_note', label: "Résumer une note", icon: "file-text" },
      { id: 'structure_note', label: "Structurer une note", icon: "list" },
      { id: 'note_to_tasks', label: "Transformer en tâches", icon: "check-square" },
      { id: 'rewrite', label: "Reformuler clairement", icon: "edit" },
      { id: 'create_plan', label: "Créer un plan", icon: "map" }
    ],
    examples: [
      "Résume cette note.",
      "Transforme cette note en plan d'action.",
      "Reformule de manière plus professionnelle.",
      "Classe mes idées principales."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // BOÎTE À IDÉES
  // ─────────────────────────────────────────────────────────────────────────
  ideas: {
    key: 'ideas',
    icon: Lightbulb,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes idées",
    intro: "Tu es dans ta **Boîte à idées**. Je peux t'aider à développer, classer, prioriser ou transformer tes idées en projet.",
    placeholder: "Demande-moi de développer une idée…",
    quickActions: [
      { id: 'develop_idea', label: "Développer une idée", icon: "lightbulb" },
      { id: 'classify_ideas', label: "Classer mes idées", icon: "folder" },
      { id: 'prioritize', label: "Prioriser les meilleures", icon: "star" },
      { id: 'idea_to_plan', label: "Transformer en plan", icon: "map" },
      { id: 'create_project', label: "Créer une fiche projet", icon: "briefcase" }
    ],
    examples: [
      "Développe cette idée.",
      "Classe mes idées par potentiel.",
      "Transforme cette idée en projet concret.",
      "Donne-moi les prochaines étapes."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MES DOCUMENTS
  // ─────────────────────────────────────────────────────────────────────────
  documents: {
    key: 'documents',
    icon: FolderOpen,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes documents",
    intro: "Tu es dans **Mes Documents**. Je peux t'aider à retrouver, résumer, analyser ou créer des documents.",
    placeholder: "Demande-moi de travailler sur tes documents…",
    quickActions: [
      { id: 'summarize_doc', label: "Résumer un document", icon: "file-text" },
      { id: 'find_file', label: "Retrouver un fichier", icon: "search" },
      { id: 'create_doc', label: "Créer un document", icon: "plus" },
      { id: 'extract_key', label: "Extraire les points clés", icon: "key" },
      { id: 'doc_to_plan', label: "Transformer en plan", icon: "map" }
    ],
    examples: [
      "Résume ce document.",
      "Retrouve le document sur ce sujet.",
      "Crée un dossier de présentation.",
      "Extrais les informations importantes."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MES CONTACTS
  // ─────────────────────────────────────────────────────────────────────────
  contacts: {
    key: 'contacts',
    icon: Users,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes contacts",
    intro: "Tu es dans **Mes Contacts**. Je peux t'aider à retrouver, organiser, enrichir ou relancer tes contacts.",
    placeholder: "Demande-moi de t'aider avec tes contacts…",
    quickActions: [
      { id: 'find_contact', label: "Trouver un contact", icon: "search" },
      { id: 'prepare_followup', label: "Préparer une relance", icon: "send" },
      { id: 'classify_contacts', label: "Classer mes contacts", icon: "folder" },
      { id: 'create_contact', label: "Créer une fiche", icon: "user-plus" },
      { id: 'contacts_to_call', label: "Contacts à rappeler", icon: "phone" }
    ],
    examples: [
      "Retrouve le contact de Pierre.",
      "Prépare une relance pour ce contact.",
      "Quels contacts dois-je rappeler ?",
      "Classe mes contacts par priorité."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // RÉSEAU SOCIAL
  // ─────────────────────────────────────────────────────────────────────────
  social: {
    key: 'social',
    icon: Globe,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes contenus",
    intro: "Tu es dans **Réseau Social**. Je peux t'aider à créer, publier, répondre ou organiser tes contenus.",
    placeholder: "Demande-moi de créer ou gérer tes contenus…",
    quickActions: [
      { id: 'create_post', label: "Créer un post", icon: "edit" },
      { id: 'reply_comments', label: "Répondre aux commentaires", icon: "message-circle" },
      { id: 'prepare_publication', label: "Préparer une publication", icon: "file-text" },
      { id: 'summarize_interactions', label: "Résumer les interactions", icon: "activity" },
      { id: 'schedule_content', label: "Planifier du contenu", icon: "calendar" }
    ],
    examples: [
      "Crée un post pour aujourd'hui.",
      "Prépare une réponse à ce commentaire.",
      "Planifie une publication.",
      "Transforme cette idée en post."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MARKETPLACE
  // ─────────────────────────────────────────────────────────────────────────
  marketplace: {
    key: 'marketplace',
    icon: ShoppingBag,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider sur la marketplace",
    intro: "Tu es dans **Marketplace**. Je peux t'aider à créer une annonce, vendre, acheter, louer, échanger ou suivre tes transactions.",
    placeholder: "Demande-moi de t'aider sur la marketplace…",
    quickActions: [
      { id: 'create_listing', label: "Créer une annonce", icon: "plus" },
      { id: 'improve_listing', label: "Améliorer une annonce", icon: "edit" },
      { id: 'track_sale', label: "Suivre une vente", icon: "package" },
      { id: 'reply_buyer', label: "Répondre à un acheteur", icon: "message-circle" },
      { id: 'compare_offers', label: "Comparer des offres", icon: "git-compare" }
    ],
    examples: [
      "Crée une annonce pour ce produit.",
      "Améliore le texte de cette annonce.",
      "Relance cet acheteur.",
      "Compare ces offres."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PRESTATAIRES
  // ─────────────────────────────────────────────────────────────────────────
  prestataires: {
    key: 'prestataires',
    icon: Wrench,
    title: "Assistant Abracada",
    subtitle: "Je peux t'aider avec tes prestataires",
    intro: "Tu es dans **Prestataires**. Je peux t'aider à trouver, comparer, contacter ou suivre tes prestataires.",
    placeholder: "Demande-moi de gérer tes prestataires…",
    quickActions: [
      { id: 'find_provider', label: "Trouver un prestataire", icon: "search" },
      { id: 'compare_quotes', label: "Comparer des devis", icon: "git-compare" },
      { id: 'prepare_request', label: "Préparer une demande", icon: "file-text" },
      { id: 'followup_provider', label: "Relancer un prestataire", icon: "send" },
      { id: 'create_followup_task', label: "Créer une tâche de suivi", icon: "check-square" }
    ],
    examples: [
      "Prépare une demande de devis.",
      "Compare ces prestataires.",
      "Relance ce prestataire.",
      "Crée une tâche de suivi."
    ]
  }
};


// ═══════════════════════════════════════════════════════════════════════════
// MODULES PRO (Abracada Pro)
// ═══════════════════════════════════════════════════════════════════════════

export const assistantContextConfigPro = {

  // ─────────────────────────────────────────────────────────────────────────
  // CLIENTS
  // ─────────────────────────────────────────────────────────────────────────
  clients: {
    key: 'clients',
    icon: UserCheck,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec tes clients",
    intro: "Tu es dans **Clients**. Je peux t'aider à gérer, relancer, organiser ou analyser tes fiches clients.",
    placeholder: "Demande-moi de t'aider avec tes clients…",
    quickActions: [
      { id: 'summarize_client', label: "Résumer une fiche client", icon: "file-text" },
      { id: 'prepare_followup', label: "Préparer une relance", icon: "send" },
      { id: 'create_appointment', label: "Créer un rendez-vous", icon: "calendar" },
      { id: 'clients_to_call', label: "Clients à rappeler", icon: "phone" },
      { id: 'generate_message', label: "Générer un message", icon: "message-circle" }
    ],
    examples: [
      "Résume la fiche de ce client.",
      "Prépare une relance commerciale.",
      "Quels clients dois-je rappeler ?",
      "Génère un message de suivi."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // DEVIS
  // ─────────────────────────────────────────────────────────────────────────
  devis: {
    key: 'devis',
    icon: FileSignature,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec tes devis",
    intro: "Tu es dans **Devis**. Je peux t'aider à créer, résumer, relancer ou optimiser tes devis.",
    placeholder: "Demande-moi de t'aider avec tes devis…",
    quickActions: [
      { id: 'create_quote', label: "Créer un devis", icon: "plus" },
      { id: 'summarize_quote', label: "Résumer un devis", icon: "file-text" },
      { id: 'followup_quote', label: "Relancer un devis", icon: "send" },
      { id: 'message_to_quote', label: "Message → Devis", icon: "arrow-right" },
      { id: 'check_missing', label: "Vérifier les infos", icon: "check-circle" }
    ],
    examples: [
      "Crée un devis pour ce client.",
      "Résume ce devis.",
      "Relance ce devis envoyé.",
      "Vérifie les informations manquantes."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FACTURES
  // ─────────────────────────────────────────────────────────────────────────
  factures: {
    key: 'factures',
    icon: Receipt,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec tes factures",
    intro: "Tu es dans **Factures**. Je peux t'aider à créer, vérifier, relancer ou résumer ta facturation.",
    placeholder: "Demande-moi de t'aider avec tes factures…",
    quickActions: [
      { id: 'create_invoice', label: "Créer une facture", icon: "plus" },
      { id: 'pending_invoices', label: "Factures en attente", icon: "clock" },
      { id: 'followup_payment', label: "Relancer un paiement", icon: "send" },
      { id: 'summarize_billing', label: "Résumer la facturation", icon: "file-text" },
      { id: 'prepare_client_message', label: "Message client", icon: "message-circle" }
    ],
    examples: [
      "Crée une facture pour ce devis.",
      "Quelles factures sont en attente ?",
      "Relance ce paiement.",
      "Résume ma facturation du mois."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // BOUTIQUE
  // ─────────────────────────────────────────────────────────────────────────
  boutique: {
    key: 'boutique',
    icon: Store,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec ta boutique",
    intro: "Tu es dans **Boutique**. Je peux t'aider à ajouter, améliorer ou optimiser tes produits et prestations.",
    placeholder: "Demande-moi de t'aider avec ta boutique…",
    quickActions: [
      { id: 'add_product', label: "Ajouter un produit", icon: "plus" },
      { id: 'improve_product', label: "Améliorer une fiche", icon: "edit" },
      { id: 'create_service', label: "Créer une prestation", icon: "briefcase" },
      { id: 'optimize_text', label: "Optimiser le texte", icon: "type" },
      { id: 'check_incomplete', label: "Produits incomplets", icon: "alert-circle" }
    ],
    examples: [
      "Ajoute ce produit à ma boutique.",
      "Améliore la description de ce produit.",
      "Crée une prestation de service.",
      "Optimise le texte de vente."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // COMMANDES
  // ─────────────────────────────────────────────────────────────────────────
  commandes: {
    key: 'commandes',
    icon: Package,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec tes commandes",
    intro: "Tu es dans **Commandes**. Je peux t'aider à résumer, suivre ou gérer tes commandes.",
    placeholder: "Demande-moi de t'aider avec tes commandes…",
    quickActions: [
      { id: 'summarize_orders', label: "Résumer les commandes", icon: "file-text" },
      { id: 'urgent_orders', label: "Commandes urgentes", icon: "alert-circle" },
      { id: 'prepare_client_message', label: "Message client", icon: "message-circle" },
      { id: 'create_followup', label: "Tâche de suivi", icon: "check-square" },
      { id: 'check_delays', label: "Vérifier les retards", icon: "clock" }
    ],
    examples: [
      "Résume mes commandes du jour.",
      "Quelles commandes sont urgentes ?",
      "Prépare un message pour ce client.",
      "Vérifie les commandes en retard."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // RÉSEAUX SOCIAUX PRO
  // ─────────────────────────────────────────────────────────────────────────
  socialPro: {
    key: 'socialPro',
    icon: Share2,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec tes réseaux",
    intro: "Tu es dans **Réseaux Sociaux Pro**. Je peux t'aider à créer, programmer ou gérer tes contenus professionnels.",
    placeholder: "Demande-moi de créer du contenu pro…",
    quickActions: [
      { id: 'create_post', label: "Créer un post", icon: "edit" },
      { id: 'reply_comments', label: "Répondre aux commentaires", icon: "message-circle" },
      { id: 'schedule_content', label: "Programmer du contenu", icon: "calendar" },
      { id: 'summarize_dms', label: "Résumer les messages", icon: "inbox" },
      { id: 'prepare_campaign', label: "Préparer une campagne", icon: "target" }
    ],
    examples: [
      "Crée un post professionnel.",
      "Réponds à ces commentaires.",
      "Programme mes posts de la semaine.",
      "Prépare une campagne marketing."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ÉQUIPE
  // ─────────────────────────────────────────────────────────────────────────
  equipe: {
    key: 'equipe',
    icon: UsersRound,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec ton équipe",
    intro: "Tu es dans **Équipe**. Je peux t'aider à assigner, coordonner ou suivre l'activité de ton équipe.",
    placeholder: "Demande-moi de t'aider avec ton équipe…",
    quickActions: [
      { id: 'assign_task', label: "Assigner une tâche", icon: "user-plus" },
      { id: 'summarize_activity', label: "Résumer l'activité", icon: "activity" },
      { id: 'internal_message', label: "Message interne", icon: "message-circle" },
      { id: 'see_urgencies', label: "Voir les urgences", icon: "alert-circle" },
      { id: 'create_planning', label: "Créer un planning", icon: "calendar" }
    ],
    examples: [
      "Assigne cette tâche à Marie.",
      "Résume l'activité de l'équipe.",
      "Prépare un message pour l'équipe.",
      "Quelles sont les urgences ?"
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // AGENTS IA
  // ─────────────────────────────────────────────────────────────────────────
  agents: {
    key: 'agents',
    icon: Bot,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec tes agents IA",
    intro: "Tu es dans **Agents IA**. Je peux t'aider à activer, configurer ou optimiser tes agents automatiques.",
    placeholder: "Demande-moi de gérer tes agents IA…",
    quickActions: [
      { id: 'activate_agent', label: "Activer un agent", icon: "power" },
      { id: 'summarize_activity', label: "Résumer l'activité", icon: "activity" },
      { id: 'create_mission', label: "Créer une mission", icon: "target" },
      { id: 'see_automations', label: "Voir les automatisations", icon: "settings" },
      { id: 'suggest_optimization', label: "Proposer une optimisation", icon: "zap" }
    ],
    examples: [
      "Active l'agent de relance clients.",
      "Résume l'activité des agents.",
      "Crée une mission pour un agent.",
      "Propose une optimisation."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // DOCUMENTS PRO
  // ─────────────────────────────────────────────────────────────────────────
  documentsPro: {
    key: 'documentsPro',
    icon: Briefcase,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec tes documents pro",
    intro: "Tu es dans **Documents Pro**. Je peux t'aider à créer, résumer ou analyser tes documents professionnels.",
    placeholder: "Demande-moi de travailler sur tes documents…",
    quickActions: [
      { id: 'create_doc', label: "Créer un document", icon: "plus" },
      { id: 'summarize_contract', label: "Résumer un contrat", icon: "file-text" },
      { id: 'prepare_folder', label: "Préparer un dossier client", icon: "folder" },
      { id: 'extract_key', label: "Extraire les points clés", icon: "key" },
      { id: 'doc_to_task', label: "Transformer en tâche", icon: "check-square" }
    ],
    examples: [
      "Crée un document de présentation.",
      "Résume ce contrat.",
      "Prépare le dossier de ce client.",
      "Extrais les informations importantes."
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PLANNING PRO
  // ─────────────────────────────────────────────────────────────────────────
  planningPro: {
    key: 'planningPro',
    icon: Clock,
    title: "Assistant Abracada Pro",
    subtitle: "Je peux t'aider avec ton planning",
    intro: "Tu es dans **Planning Pro**. Je peux t'aider à organiser, planifier ou optimiser ton planning professionnel.",
    placeholder: "Demande-moi de gérer ton planning…",
    quickActions: [
      { id: 'organize_day', label: "Organiser la journée", icon: "sun" },
      { id: 'plan_intervention', label: "Planifier une intervention", icon: "map-pin" },
      { id: 'find_slot', label: "Trouver un créneau", icon: "search" },
      { id: 'move_appointment', label: "Déplacer un RDV", icon: "move" },
      { id: 'prepare_week', label: "Préparer la semaine", icon: "calendar" }
    ],
    examples: [
      "Organise ma journée de demain.",
      "Planifie cette intervention.",
      "Trouve un créneau pour ce client.",
      "Prépare mon planning de la semaine."
    ]
  }
};


// ═══════════════════════════════════════════════════════════════════════════
// CONTEXTE PAR DÉFAUT (si page non reconnue)
// ═══════════════════════════════════════════════════════════════════════════

export const defaultAssistantContext = {
  key: 'default',
  icon: null,
  title: "Assistant Abracada",
  subtitle: "Je suis là pour t'aider",
  intro: "Je suis connecté à cette page. Tu peux me demander de créer, résumer, organiser ou automatiser ce qui se trouve ici.",
  placeholder: "Écris ta demande…",
  quickActions: [
    { id: 'help', label: "Comment tu peux m'aider ?", icon: "help-circle" },
    { id: 'summarize', label: "Résumer cette page", icon: "file-text" },
    { id: 'create_task', label: "Créer une tâche", icon: "check-square" },
    { id: 'ask_question', label: "Poser une question", icon: "message-circle" }
  ],
  examples: []
};


// ═══════════════════════════════════════════════════════════════════════════
// FONCTION UTILITAIRE : Récupérer le contexte selon la page active
// ═══════════════════════════════════════════════════════════════════════════

export const getAssistantContext = (contextKey, isPro = false) => {
  // D'abord chercher dans la config Pro si isPro
  if (isPro && assistantContextConfigPro[contextKey]) {
    return assistantContextConfigPro[contextKey];
  }
  
  // Ensuite chercher dans la config standard
  if (assistantContextConfig[contextKey]) {
    return assistantContextConfig[contextKey];
  }
  
  // Sinon retourner le contexte par défaut
  return defaultAssistantContext;
};


// ═══════════════════════════════════════════════════════════════════════════
// MAPPING : Contexte actif → Clé de configuration
// ═══════════════════════════════════════════════════════════════════════════

export const contextKeyMapping = {
  // Modules Particulier
  'taches': 'tasks',
  'liste_taches': 'tasks',
  'liste des taches': 'tasks',
  'taches partagees': 'tasks',
  'taches_partagees': 'tasks',
  'messages': 'messages',
  'mes messages': 'messages',
  'messagerie': 'messages',
  'emails': 'emails',
  'mes emails': 'emails',
  'mail': 'emails',
  'mails': 'emails',
  'agenda': 'agenda',
  'mon agenda': 'agenda',
  'agenda partage': 'agenda',
  'calendrier': 'agenda',
  'visio': 'visio',
  'visioconference': 'visio',
  'notes': 'notes',
  'mes notes': 'notes',
  'idees': 'ideas',
  'boite_idees': 'ideas',
  'boite a idees': 'ideas',
  'documents': 'documents',
  'mes documents': 'documents',
  'fichiers': 'documents',
  'contacts': 'contacts',
  'mes contacts': 'contacts',
  'carnet': 'contacts',
  'social': 'social',
  'reseau': 'social',
  'reseau social': 'social',
  'groupes publics': 'social',
  'groupes prives': 'social',
  'marketplace': 'marketplace',
  'annonces': 'marketplace',
  'prestataires': 'prestataires',
  
  // Modules Pro
  'clients': 'clients',
  'mes clients': 'clients',
  'devis': 'devis',
  'mes devis': 'devis',
  'factures': 'factures',
  'mes factures': 'factures',
  'boutique': 'boutique',
  'ma boutique': 'boutique',
  'produits': 'boutique',
  'commandes': 'commandes',
  'mes commandes': 'commandes',
  'reseaux_sociaux': 'socialPro',
  'reseaux sociaux': 'socialPro',
  'marketing': 'socialPro',
  'equipe': 'equipe',
  'mon equipe': 'equipe',
  'team': 'equipe',
  'tableau de bord': 'equipe',
  'agents': 'agents',
  'agents_ia': 'agents',
  'agents ia': 'agents',
  'documents_pro': 'documentsPro',
  'documents pro': 'documentsPro',
  'planning': 'planningPro',
  'planning_pro': 'planningPro',
  'pointage': 'planningPro',
  'pointage rh': 'planningPro',
  'pointage manager': 'planningPro'
};

export const resolveContextKey = (activeContext) => {
  if (!activeContext) return 'default';
  
  // Normaliser : minuscules, remplacer accents, retirer underscores
  const normalized = activeContext.toLowerCase()
    .replace(/[éèê]/g, 'e')
    .replace(/[àâ]/g, 'a')
    .replace(/[ùû]/g, 'u')
    .replace(/[îï]/g, 'i')
    .replace(/[ôö]/g, 'o')
    .replace(/_/g, ' ')
    .trim();
  
  // Chercher correspondance directe
  if (contextKeyMapping[normalized]) {
    return contextKeyMapping[normalized];
  }
  
  // Chercher correspondance partielle
  for (const [key, value] of Object.entries(contextKeyMapping)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return value;
    }
  }
  
  return 'default';
};
